import { PostHog } from 'posthog-js'
import { FontSlot } from '@storefronts/common-types'
import {
    AnalyticsEvent,
    EventName,
    EventPropertiesMap,
} from './analyticsEvents'

export class AnalyticsService {
    private posthog: PostHog | null = null
    private eventQueue: AnalyticsEvent[] = []
    private isDevelopment = process.env.NODE_ENV !== 'development'

    initialize(posthogInstance: PostHog | null) {
        this.posthog = posthogInstance

        // Process any queued events
        this.processEventQueue()
    }

    private processEventQueue() {
        if (!this.posthog) return

        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift()
            if (event) {
                this.trackEvent(event.name, event.properties)
            }
        }
    }

    private trackEvent<T extends EventName>(
        eventName: T,
        properties: EventPropertiesMap[T],
    ) {
        if (!this.posthog) {
            if (this.isDevelopment) {
                console.warn(
                    'PostHog not initialized, queueing event:',
                    eventName,
                    properties,
                )
            }
            this.eventQueue.push({
                name: eventName,
                properties,
            } as AnalyticsEvent)
            return
        }

        // Enrich properties with standard fields
        const enrichedProperties = {
            ...properties,
            timestamp: new Date().toISOString(),
            environment: process.env.NODE_ENV,
        }

        if (this.isDevelopment) {
            console.log('📊 Analytics Event:', eventName, enrichedProperties)
        }

        try {
            this.posthog.capture(eventName, enrichedProperties)
        } catch (error) {
            console.error('Failed to track event:', eventName, error)
        }
    }

    // Navigation & Global Actions
    trackNavItemClick(section: string, item: string) {
        this.trackEvent('nav_item_click', { section, item })
    }

    // Authentication
    trackLoginClick(method: 'google' | 'magic_link' | 'password') {
        this.trackEvent('login_click', { method })
    }

    trackSignupClick(method: 'google' | 'magic_link' | 'password') {
        this.trackEvent('signup_click', { method })
    }

    trackMagicLinkSendClick() {
        this.trackEvent('magic_link_send_click', {})
    }

    trackLandingPageCopyLinkClick(pageId?: string) {
        this.trackEvent('landing_page_copy_link_click', { page_id: pageId })
    }

    trackLandingPagePreviewClick(pageId?: string) {
        this.trackEvent('landing_page_preview_click', { page_id: pageId })
    }

    trackLandingPageEditClick(pageId?: string) {
        this.trackEvent('landing_page_edit_click', { page_id: pageId })
    }

    // Content Blocks
    trackContentBlockAdd(type: string, blockId?: string, position?: number) {
        this.trackEvent('content_block_add', {
            type,
            block_id: blockId,
            position,
        })
    }

    trackContentBlockEdit(type: string, blockId?: string, position?: number) {
        this.trackEvent('content_block_edit', {
            type,
            block_id: blockId,
            position,
        })
    }

    trackContentBlockDelete(type: string, blockId?: string, position?: number) {
        this.trackEvent('content_block_delete', {
            type,
            block_id: blockId,
            position,
        })
    }

    // Video Management
    trackVideoUploadClick(type: 'single' | 'multi', videoCount?: number) {
        this.trackEvent('video_upload_click', { type, video_count: videoCount })
    }

    trackVideoSaveClick(videoId?: string) {
        this.trackEvent('video_save_click', { video_id: videoId })
    }

    trackVideoDeleteClick(videoId?: string) {
        this.trackEvent('video_delete_click', { video_id: videoId })
    }

    // Product Management
    trackProductImportClick(source?: 'shopify' | 'manual' | 'csv') {
        this.trackEvent('product_import_click', { source })
    }

    trackProductClick(productId?: string) {
        this.trackEvent('product_click', { product_id: productId })
    }

    trackProductImportFromShopifyClick() {
        this.trackEvent('product_import_from_shopify_click', {
            source: 'shopify',
        })
    }

    // Link Management
    trackLinkCreateClick(linkType?: string) {
        this.trackEvent('link_create_click', { link_type: linkType })
    }

    trackLinkEditClick(linkId?: string, linkType?: string) {
        this.trackEvent('link_edit_click', {
            link_id: linkId,
            link_type: linkType,
        })
    }

    trackLinkCopyClick(linkId?: string, linkType?: string) {
        this.trackEvent('link_copy_click', {
            link_id: linkId,
            link_type: linkType,
        })
    }

    // Persona Management
    trackPersonaCreateClick() {
        this.trackEvent('persona_create_click', { generation_method: 'manual' })
    }

    trackPersonaEditClick(personaId?: string, personaName?: string) {
        this.trackEvent('persona_edit_click', {
            persona_id: personaId,
            persona_name: personaName,
        })
    }

    trackPersonaGenerateClick(personaId?: string) {
        this.trackEvent('persona_generate_click', {
            persona_id: personaId,
            generation_method: 'ai',
        })
    }

    // Settings
    trackSettingsTabClick(tabName: string) {
        this.trackEvent('settings_tab_click', { tab_name: tabName })
    }

    trackSettingsSaveClick(section?: string) {
        this.trackEvent('settings_save_click', { section: section as any })
    }

    trackSettingsFontChange(fontType: FontSlot, fontName: string) {
        this.trackEvent('settings_font_change', {
            font_type: fontType,
            font_name: fontName,
        })
    }

    trackBrandColorChange(colorType: 'primary' | 'secondary' | 'accent') {
        this.trackEvent('brand_color_change', {
            section: 'brand',
            color_type: colorType,
        })
    }

    trackBrandFontChange(fontType: 'primary' | 'secondary') {
        this.trackEvent('brand_font_change', {
            section: 'brand',
            font_type: fontType,
        })
    }

    trackLogoUploadClick() {
        this.trackEvent('logo_upload_click', { section: 'brand' })
    }

    // Team Management
    trackTeamInviteClick() {
        this.trackEvent('team_invite_click', {})
    }

    trackTeamInviteSend(userEmail: string, userRole: string) {
        this.trackEvent('team_invite_send', {
            user_email: userEmail,
            user_role: userRole,
        })
    }

    trackTeamRemoveClick(userEmail: string, userRole: string) {
        this.trackEvent('team_remove_click', {
            user_email: userEmail,
            user_role: userRole,
        })
    }

    trackTeamRoleChange(userEmail: string, oldRole: string, newRole: string) {
        this.trackEvent('team_role_change', {
            user_email: userEmail,
            old_role: oldRole,
            new_role: newRole,
        })
    }

    trackTeamResendInvite(userEmail: string, userRole: string) {
        this.trackEvent('team_resend_invite', {
            user_email: userEmail,
            user_role: userRole,
        })
    }

    // Opportunity Card
    trackOpportunityCardActionClick(
        adName?: string,
        phase?: string,
        impact?: string,
        opportunityType?: string,
    ) {
        this.trackEvent('opportunity_card_action_click', {
            ad_name: adName,
            phase: phase,
            impact: impact,
            opportunity_type: opportunityType,
        })
    }

    // Utility methods
    identifyUser(userId: string, userProperties?: Record<string, any>) {
        if (!this.posthog) {
            console.warn('PostHog not initialized, cannot identify user')
            return
        }

        this.posthog.identify(userId, userProperties)
    }

    setUserProperties(properties: Record<string, any>) {
        if (!this.posthog) {
            console.warn('PostHog not initialized, cannot set user properties')
            return
        }

        this.posthog.people.set(properties)
    }

    reset() {
        if (!this.posthog) return
        this.posthog.reset()
    }
}
